import type {
  PriceStrategy,
  PublicationStatus,
  ListingStatus,
} from "@/lib/channels";

/**
 * Provider-neutral channel pricing & publication-state helpers.
 *
 * Pure and dependency-free: channel price derivation, base-price propagation,
 * and the GREEN/AMBER/RED/GREY publication signal are all deterministic
 * functions of canonical data. No provider IDs, no API calls, no secrets.
 */

// ---------------------------------------------------------------------------
// Rounding
// ---------------------------------------------------------------------------

export function roundPrice(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

// ---------------------------------------------------------------------------
// Price strategy computation
// ---------------------------------------------------------------------------

/** The parameters that shape a non-inherit price strategy. */
export interface PriceStrategyConfig {
  /** Absolute channel price (fixed_override). */
  fixedOverride?: number | null;
  /** Percentage markup, e.g. 10 = +10% (percentage_markup). */
  percentageMarkup?: number | null;
  /** Absolute amount added on top of base (fixed_markup). */
  fixedMarkup?: number | null;
}

/**
 * Which strategies depend on the canonical base price. Fixed overrides are
 * deliberately excluded: a base-price change must never move them.
 */
export function isBaseDependentStrategy(strategy: PriceStrategy): boolean {
  return (
    strategy === "inherit" ||
    strategy === "percentage_markup" ||
    strategy === "fixed_markup"
  );
}

/**
 * Derive the intended channel price for a single variant from a base price and
 * the listing's price strategy. This is the single source of truth for the
 * "show the calculated intended price before publication" requirement.
 */
export function computeChannelPrice(
  strategy: PriceStrategy,
  basePrice: number,
  config: PriceStrategyConfig = {},
): number {
  const base = Number(basePrice ?? 0);

  switch (strategy) {
    case "inherit":
      return roundPrice(base);

    case "fixed_override": {
      const override = config.fixedOverride;
      return override == null ? roundPrice(base) : roundPrice(Number(override));
    }

    case "percentage_markup": {
      const pct = Number(config.percentageMarkup ?? 0);
      return roundPrice(base * (1 + pct / 100));
    }

    case "fixed_markup": {
      const markup = Number(config.fixedMarkup ?? 0);
      return roundPrice(base + markup);
    }

    default:
      return roundPrice(base);
  }
}

/**
 * Per-listing `channel_listing_variants.channel_price` is the publish override.
 * Listing strategy applies only when that override is unset. Does not change
 * product or sibling-listing prices.
 */
export function intendedProviderPrice(
  strategy: PriceStrategy,
  basePrice: number,
  config: PriceStrategyConfig = {},
  channelPrice?: number | null,
): number {
  if (channelPrice != null && Number.isFinite(Number(channelPrice))) {
    return roundPrice(Number(channelPrice));
  }
  return computeChannelPrice(strategy, basePrice, config);
}

/**
 * BigCommerce variation products still expose a parent catalog `price`.
 * PRICE-domain sync writes the channel-adjusted product base so the parent
 * is not left at a stale historical markup while variants already match.
 */
export function bcVariationParentPrice(
  strategy: PriceStrategy,
  productBase: number,
  config: PriceStrategyConfig = {},
): number {
  return computeChannelPrice(strategy, productBase, config);
}

export function shouldWriteBcParentPrice(opts: {
  variantBindingCount: number;
  parentBefore: number;
  intendedParent: number;
}): boolean {
  return opts.variantBindingCount > 0 && opts.parentBefore !== opts.intendedParent;
}

/** Negative adjustments are valid. Only a non-positive result is rejected. */
export function isValidSellingPrice(price: number): boolean {
  return Number.isFinite(price) && price > 0;
}

export function sellingPriceError(price: number): string | null {
  if (!Number.isFinite(price)) return "Calculated selling price is invalid.";
  if (price <= 0) return "Selling price must be greater than zero.";
  return null;
}

// ---------------------------------------------------------------------------
// Base-price propagation
// ---------------------------------------------------------------------------

export interface PropagationImpact {
  /** True when this strategy's channel price moves with a base-price change. */
  dependsOnBase: boolean;
  previousPrice: number;
  nextPrice: number;
  delta: number;
}

/**
 * Compute how a canonical base-price change propagates to a channel price.
 * Base-dependent strategies (inherit / percentage_markup / fixed_markup) are
 * recalculated; fixed_override is left untouched (delta === 0).
 */
export function computePropagationImpact(
  strategy: PriceStrategy,
  oldBasePrice: number,
  newBasePrice: number,
  config: PriceStrategyConfig = {},
): PropagationImpact {
  const previousPrice = computeChannelPrice(strategy, oldBasePrice, config);
  const nextPrice = computeChannelPrice(strategy, newBasePrice, config);
  return {
    dependsOnBase: isBaseDependentStrategy(strategy),
    previousPrice,
    nextPrice,
    delta: roundPrice(nextPrice - previousPrice),
  };
}

// ---------------------------------------------------------------------------
// provider_config persistence helpers (pricing namespace)
// ---------------------------------------------------------------------------

function asFiniteNumber(value: unknown): number | null {
  const n =
    typeof value === "number"
      ? value
      : typeof value === "string" && value.trim() !== ""
        ? Number(value)
        : NaN;
  return Number.isFinite(n) ? n : null;
}

/** Read a PriceStrategyConfig out of a listing's provider_config.pricing. */
export function pricingConfigFromProviderConfig(
  providerConfig: Record<string, unknown> | null | undefined,
): PriceStrategyConfig {
  const pricing = (providerConfig?.pricing ?? {}) as Record<string, unknown>;
  return {
    fixedOverride: asFiniteNumber(pricing.fixed_override),
    percentageMarkup: asFiniteNumber(pricing.percentage_markup),
    fixedMarkup: asFiniteNumber(pricing.fixed_markup),
  };
}

const PRICE_STRATEGIES: PriceStrategy[] = [
  "inherit",
  "fixed_override",
  "fixed_markup",
  "percentage_markup",
];

/** Hydrate the listing Pricing form from stored strategy — never from a default inherit. */
export function storedPriceFormState(
  strategy: string | null | undefined,
  config: PriceStrategyConfig,
): { rule: PriceStrategy; amount: string } {
  const rule = PRICE_STRATEGIES.includes(strategy as PriceStrategy) ? (strategy as PriceStrategy) : "inherit";
  if (rule === "inherit") return { rule, amount: "" };
  const n =
    rule === "fixed_override"
      ? config.fixedOverride
      : rule === "percentage_markup"
        ? config.percentageMarkup
        : config.fixedMarkup;
  return { rule, amount: n == null ? "" : String(n) };
}

/** Draft form values use the same PriceStrategyConfig shape as computeChannelPrice. */
export function priceFormConfig(rule: PriceStrategy, amountRaw: string): PriceStrategyConfig {
  const n = amountRaw.trim() === "" ? NaN : Number(amountRaw);
  const amount = Number.isFinite(n) ? n : null;
  return {
    fixedOverride: rule === "fixed_override" ? amount : null,
    percentageMarkup: rule === "percentage_markup" ? amount : null,
    fixedMarkup: rule === "fixed_markup" ? amount : null,
  };
}

export function formatPriceAdjustmentLine(rule: PriceStrategy, config: PriceStrategyConfig): string {
  if (rule === "inherit") return "Adjustment none (same as base)";
  if (rule === "percentage_markup") {
    const pct = Number(config.percentageMarkup ?? 0);
    const sign = pct < 0 ? "-" : "+";
    return `Adjustment ${sign}${Math.abs(pct)}%`;
  }
  if (rule === "fixed_override") {
    return `Fixed price A$${Number(config.fixedOverride ?? 0).toLocaleString("en-AU", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  }
  const amt = Number(config.fixedMarkup ?? 0);
  const sign = amt < 0 ? "-" : "+";
  return `Adjustment ${sign}A$${Math.abs(amt).toLocaleString("en-AU", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

/** Build the provider_config fragment that persists a strategy's parameter. */
export function buildPricingProviderConfig(
  strategy: PriceStrategy,
  value: number | null | undefined,
): Record<string, unknown> {
  const pricing: Record<string, unknown> = {};
  if (strategy === "fixed_override") pricing.fixed_override = Number(value ?? 0);
  else if (strategy === "percentage_markup") pricing.percentage_markup = Number(value ?? 0);
  else if (strategy === "fixed_markup") pricing.fixed_markup = Number(value ?? 0);
  return { pricing };
}

// ---------------------------------------------------------------------------
// Publication state signal (GREEN / AMBER / RED / GREY)
// ---------------------------------------------------------------------------

export type PublicationSignal = "green" | "amber" | "red" | "grey";

export interface PublicationStateMeta {
  signal: PublicationSignal;
  /** Human label — never rely on colour alone. */
  label: string;
  /** Remix icon class for the status icon. */
  icon: string;
  description: string;
}

export interface PublicationStateInput {
  publicationStatus?: PublicationStatus | null;
  status?: ListingStatus | null;
  /** True when canonical data has drifted and needs re-sync. */
  hasDirty?: boolean;
  /** True when the connector is authorized/connected. */
  connectionReady?: boolean;
  lastError?: string | null;
  /**
   * Provider-confirmed remote listing (e.g. BigCommerce external_product_id).
   * Authoritative readback must display as listed even if publication_status
   * lagged at not_published.
   */
  providerConfirmedListed?: boolean;
}

/**
 * Collapse the raw publication fields into a single, obvious state.
 * - GREEN  — listed & synced
 * - AMBER  — needs attention / needs sync (drift, dirty, or in-flight)
 * - RED    — failed / not listed where expected
 * - GREY   — not selected / not configured
 * Always carries an icon + text label so it is never colour-only.
 */
export function publicationStateMeta(input: PublicationStateInput): PublicationStateMeta {
  const { publicationStatus, status, hasDirty, connectionReady, lastError, providerConfirmedListed } = input;

  // Terminal / hidden lifecycle states take precedence over sync state, so an
  // archived listing never renders as "Listed · Synced".
  if (status === "archived") {
    return {
      signal: "grey",
      label: "Archived",
      icon: "ri-archive-line",
      description: "Listing archived",
    };
  }

  if (status === "ended") {
    return {
      signal: "grey",
      label: "Ended",
      icon: "ri-stop-circle-line",
      description: "Listing ended",
    };
  }

  if (status === "paused") {
    return {
      signal: "amber",
      label: "Paused",
      icon: "ri-pause-circle-line",
      description: "Listing paused",
    };
  }

  if (status === "publishing") {
    return {
      signal: "amber",
      label: "Publishing…",
      icon: "ri-loader-4-line",
      description: "Publication in progress",
    };
  }

  if (publicationStatus === "remote_review") {
    return {
      signal: "red",
      label: "Reconciliation required",
      icon: "ri-search-eye-line",
      description: "Provider outcome is unknown; reconcile before retrying",
    };
  }

  if (
    publicationStatus === "sync_failed" ||
    status === "error" ||
    (lastError && publicationStatus !== "published" && !providerConfirmedListed)
  ) {
    return {
      signal: "red",
      label: "Failed",
      icon: "ri-close-circle-line",
      description: "Publication failed or errored",
    };
  }

  if (publicationStatus === "drift") {
    return {
      signal: "amber",
      label: "Needs sync",
      icon: "ri-alert-line",
      description: "Provider state has drifted from canonical data",
    };
  }

  if (publicationStatus === "published" || providerConfirmedListed) {
    if (hasDirty) {
      return {
        signal: "amber",
        label: "Needs sync",
        icon: "ri-alert-line",
        description: "Canonical changes not yet synced",
      };
    }
    return {
      signal: "green",
      label: "Listed · Synced",
      icon: "ri-check-double-line",
      description: "Listed and in sync with the channel",
    };
  }

  // not_published or unknown
  return {
    signal: "grey",
    label: "Not published",
    icon: "ri-circle-line",
    description: connectionReady === false ? "Channel not configured" : "Not selected for publication",
  };
}
